/**
 * AssetAllocationOverview - Consolidated Asset Allocation section
 * Extracted from ConsolidatedHoldings.tsx (TASK-371 decomposition)
 *
 * Renders the asset allocation overview with chart toggle (pie/bar)
 * and a 2-column grid for Asset Classes and Currency Exposure.
 */

import React from 'react';
import { PieChart, BarChart3 } from 'lucide-react';
import AllocationPieChart from '../molecules/AllocationPieChart';
import AllocationBarChart from '../molecules/AllocationBarChart';

export interface AssetAllocationOverviewProps {
  data: Array<{ name: string; value: number }>;
  currencyData: Array<{ name: string; value: number }>;
  chartType: 'pie' | 'bar';
  onChartTypeChange: (type: 'pie' | 'bar') => void;
}

const AssetAllocationOverview: React.FC<AssetAllocationOverviewProps> = ({
  data,
  currencyData,
  chartType,
  onChartTypeChange,
}) => {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header with Chart Toggle */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          Consolidated Asset Allocation
        </h3>
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            onClick={() => onChartTypeChange('pie')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              chartType === 'pie'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <PieChart className="w-4 h-4" />
            Pie Chart
          </button>
          <button
            onClick={() => onChartTypeChange('bar')}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              chartType === 'bar'
                ? 'bg-kielis-navy text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="w-4 h-4" />
            Bar Chart
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {chartType === 'pie' ? (
          <>
            <AllocationPieChart
              data={data}
              title="Asset Classes"
            />
            <AllocationPieChart
              data={currencyData}
              title="Currency Exposure"
            />
          </>
        ) : (
          <>
            <AllocationBarChart
              data={data}
              title="Asset Classes"
              showLegend={false}
            />
            <AllocationBarChart
              data={currencyData}
              title="Currency Exposure"
              showLegend={false}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default AssetAllocationOverview;
